<template>
  <DialogDrawer
    ref="dialogtest"
    data-testid="invitations-dialog"
    :dismissible="false"
    placement="centre"
    modal
  >
    <template #title>
      {{ translate(language, invitations.length > 1 ? 'New Invitations' : 'New Invitation') }}
    </template>
    <BootstrapAlert v-if="mustAcceptInvitation" variant="secondary">
      <p class="m-0">
        {{
          translate(
            language,
            'Your user is not a member of any customer. To continue, please accept an invitation.',
          )
        }}
      </p>
    </BootstrapAlert>
    <p class="m-0">{{ content }}</p>
    <template #buttons>
      <BootstrapButton
        v-if="mustAcceptInvitation && activeIndex === invitations.length - 1"
        name="log-out"
        variant="secondary"
        @click="$emit('logout')"
      >
        {{ translate(language, 'Log out') }}
      </BootstrapButton>
      <BootstrapButton
        v-if="
          mustAcceptInvitation && activeIndex === invitations.length - 1 && invitations.length > 1
        "
        name="retry"
        variant="ghost"
        class="ms-auto me-1"
        @click="activeIndex = 0"
      >
        {{ translate(language, 'Restart') }}
      </BootstrapButton>
      <BootstrapButton
        v-else-if="
          mustAcceptInvitation === false ||
          (mustAcceptInvitation && activeIndex < invitations.length - 1)
        "
        name="ignore"
        variant="ghost"
        class="ms-auto me-1"
        @click="ignore"
      >
        {{ translate(language, 'Ignore') }}
      </BootstrapButton>
      <BootstrapButton name="accept" @click="next">
        {{
          translate(
            language,
            invitations.length > 1 && mustAcceptInvitation === false ? 'Review' : 'Accept',
          )
        }}
      </BootstrapButton>
    </template>
  </DialogDrawer>
</template>
<script setup lang="ts">
import {translate} from '@/translate';
import DialogDrawer from '@/components/shared/custom/DialogDrawer/DialogDrawer.vue';
import type {Invitation} from '@/stores/apolloPlatform/invitations/invitations';

const props = defineProps<{open: boolean; invitations: Invitation[]}>();
const emits = defineEmits<{close: []; logout: []; review: []}>();

const route = useRoute();
const {axiosApiInstance} = useApiInstance();
const customersStore = useCustomersStore();
const userSettingsStore = useUserSettingsStore();
const language = computed(() => userSettingsStore.userSettings.language);

const dialog = useTemplateRef<InstanceType<typeof DialogDrawer>>('dialogtest');

watch(
  () => props.open,
  (value) => {
    if (value) dialog.value?.openModal();
    else dialog.value?.close();
  },
);

const getCustomerNames = (invitation: Invitation) => {
  let names = '';
  for (let i = 0; i < invitation?.customers.length; i++) {
    names += invitation.customers[i].name + ', \n';
  }
  names = names.slice(0, -3);
  return `${translate(language.value, `You have been invited to `)}${names}.`;
};

/**
 * On new login, users may have invitations but no customers.
 * In this scenario, cycle through the invitations until one is accepted.
 * Alternatively, force the user to log out.
 */

const activeIndex = ref(0);
const mustAcceptInvitation = computed(
  () =>
    props.invitations.length > 0 &&
    customersStore.selectedCustomer?.customer?.debtor_number === undefined,
);

const content = computed(() => {
  if (mustAcceptInvitation.value) {
    return getCustomerNames(props.invitations[activeIndex.value]);
  } else {
    return props.invitations.length > 1
      ? translate(language.value, 'Multiple invitations are awaiting review.')
      : getCustomerNames(props.invitations[0]);
  }
});

/**
 * This is old, don't use axiosApiInstance()!
 */
const loading = ref(false);
const acceptInvitation = async (invitation: Invitation) => {
  loading.value = true;
  // For each customer in invitation add name string to this.customers
  const customers: Customer[] = (customersStore.customers as Customer[]) || [];
  if (invitation.customers) {
    invitation.customers.forEach((customer: any) => customers.push(customer));
  }
  customersStore.setCustomers(customers);
  const config = {
    method: 'put',
    url: ['invitations/', invitation.id, '/accept/'].join(''),
  };
  await axiosApiInstance(config).then(async () => {
    if (invitation.customers && invitation.customers.length > 0) {
      await customersStore.readCustomerById(invitation.customers[0].id);
    }
    loading.value = false;
    // Redirect user to homepage if current page is `not-found`
    if (route.name === 'not-found') {
      location.href = '/home';
    } else {
      location.reload();
    }
  });
};

const next = () => {
  emits('close');
  if (mustAcceptInvitation.value) {
    // Don't close but cycle through invitations
    acceptInvitation(props.invitations[activeIndex.value]);
  } else {
    if (props.invitations.length > 1) {
      emits('review');
    } else {
      acceptInvitation(props.invitations[0]);
    }
  }
};

const ignore = () => {
  if (mustAcceptInvitation.value) {
    activeIndex.value++;
  } else {
    emits('close');
  }
};
</script>
